import { Flex, rem, Skeleton, Text } from '@mantine/core';
import { personDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DATE_FORMATS, formatBirthday } from '@dealstack/shared/lib/format/dates/date';
import {
	getCountryNameByCode,
	getNationalityNamesByCodes,
} from '@dealstack/shared/lib/countries/countriesData';
import { PhoneNumber } from '@dealstack/frontend/shared/components/PhoneNumber/PhoneNumber';
import { GetInvestorDetailsWithPersonRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import { InvestorDetailsRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsRow/InvestorDetailsRow.tsx';
import { DetailsChip } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsChip.tsx';

interface Props {
	dealId: string;
	investor: GetInvestorDetailsWithPersonRes | undefined;
	isLoading?: boolean;
}

export const InvestorProfileCard = ({ dealId, investor, isLoading = false }: Props) => {
	const tMipManage = useTranslationMipManage();

	if (isLoading) {
		return <Skeleton height={rem(450)} radius="0.5rem" />;
	}

	const person = investor?.person;

	const nationalities = getNationalityNamesByCodes(person?.citizenship);

	const directoryProfilePath = person?.id
		? personDetailsPath({ dealId, personId: person.id })
		: undefined;

	return (
		<InvestorDetailsCard
			title={tMipManage('investors.details.profile_card.title')}
			action={directoryProfilePath ? { kind: 'link', to: directoryProfilePath } : undefined}
			dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PROFILE_CARD}
		>
			<Flex direction="column" gap="0.25rem">
				<InvestorDetailsRow
					label={tMipManage('investors.details.profile_card.full_name')}
					value={person?.displayName}
					copyValue={person?.displayName}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PROFILE_FULL_NAME}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.details.profile_card.email')}
					value={person?.email}
					copyValue={person?.email}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PROFILE_EMAIL}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.expanded_row.phone_number')}
					value={
						person?.telephonePrefix && person?.phoneNumber ? (
							<Text fw={600}>
								<PhoneNumber
									telephonePrefix={person.telephonePrefix}
									phoneNumber={person.phoneNumber}
								/>
							</Text>
						) : undefined
					}
					copyValue={
						person?.telephonePrefix && person?.phoneNumber
							? `${person.telephonePrefix}${person.phoneNumber}`
							: undefined
					}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PROFILE_PHONE_NUMBER}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.expanded_row.date_of_birth')}
					value={formatBirthday(person?.dateOfBirth, DATE_FORMATS.CALENDAR_DATE)}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.expanded_row.country_of_birth')}
					value={getCountryNameByCode(person?.countryOfBirth) || undefined}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.expanded_row.place_of_birth')}
					value={person?.placeOfBirth}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.expanded_row.citizenship')}
					value={
						nationalities.length > 0
							? nationalities.map((nationality) => (
									<DetailsChip key={nationality} label={nationality} />
								))
							: undefined
					}
				/>
			</Flex>
		</InvestorDetailsCard>
	);
};
